import React, { useEffect, useState } from 'react';
import { get } from 'lodash';

import { Button, Input } from '@/kaya';
import { authTypes } from '@/types';
import { useArtistStore } from '@/store';
import { authHelpers, generalHelpers } from '@/helpers';
import { userActions } from '@/actions';

import styles from './BankDetails.module.scss';

const BankDetails = () => {
  const { setArtist, artist } = useArtistStore();
  const [toggle, setToggle] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<authTypes.BankInfo>(
    authTypes.INITIAL_BANK_INFO_VALUES,
  );

  const [err, setErr] = React.useState({
    bank_name: '',
    acc_no: '',
    acc_holder_name: '',
    cin_no: '',
    iban: '',
    swift_code: '',
    branch_code: '',
  });

  const handleChange = (id: string | object) => (value: string | object | boolean | File) => {
    if (typeof id !== 'object') {
      setFormValues({
        ...formValues,
        [id]: value,
      });
      setErr({
        ...err,
        [id]: '',
      });
    } else {
      setFormValues({
        ...formValues,
        ...id,
      });
    }
  };

  const disableButton = () => {
    if (
      get(formValues, 'bank_name', '') === get(artist, 'bank_name', '') &&
      get(formValues, 'acc_no', '') === get(artist, 'acc_no', '').toString() &&
      get(formValues, 'acc_holder_name', '') === get(artist, 'acc_holder_name', '') &&
      get(formValues, 'cin_no', '') === get(artist, 'cin_no', '').toString() &&
      get(formValues, 'iban', '') === get(artist, 'iban', '') &&
      get(formValues, 'swift_code', '') === get(artist, 'swift_code', '') &&
      get(formValues, 'branch_code', '') === get(artist, 'branch_code', '')
    ) {
      return true;
    }
    return false;
  };

  const handleSave = async () => {
    const error = {
      bank_name: authHelpers.validateBankName(get(formValues, 'bank_name', '')),
      acc_no: authHelpers.validateAccountNo(get(formValues, 'acc_no', '').toString()),
      acc_holder_name: authHelpers.validateAccountHolderName(
        get(formValues, 'acc_holder_name', ''),
      ),
      cin_no: authHelpers.validateCinNumber(get(formValues, 'cin_no', '').toString()),
      iban: authHelpers.validateIbanNumber(get(formValues, 'iban', '')),
      swift_code: authHelpers.validateSwiftCode(get(formValues, 'swift_code', '')),
      branch_code: authHelpers.validateBranchName(get(formValues, 'branch_code', '')),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr({
        ...err,
        ...error,
      });
      return;
    }

    setLoading(true);
    const data = await userActions.updateArtistAction({
      payload: {
        bank_name: get(formValues, 'bank_name', ''),
        acc_no: Number(get(formValues, 'acc_no', '')),
        acc_holder_name: get(formValues, 'acc_holder_name', ''),
        cin_no: Number(get(formValues, 'cin_no', '')),
        iban: get(formValues, 'iban', ''),
        swift_code: get(formValues, 'swift_code', ''),
        branch_code: get(formValues, 'branch_code', ''),
      },
      id: get(artist, 'id', ''),
    });
    if (get(data, 'success')) {
      setArtist(data.data);
    }
    setToggle(!toggle);
    setLoading(false);
  };

  useEffect(() => {
    setFormValues({
      ...formValues,
      bank_name: get(artist, 'bank_name', ''),
      acc_no: get(artist, 'acc_no', '').toString(),
      acc_holder_name: get(artist, 'acc_holder_name', ''),
      cin_no: get(artist, 'cin_no', '').toString(),
      iban: get(artist, 'iban', ''),
      swift_code: get(artist, 'swift_code', ''),
      branch_code: get(artist, 'branch_code', ''),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.BankDetails}>
      <div className={styles.inputLabel}>
        <p className={styles.label}>Bank Name</p>
        <Input
          placeholder="Bank Name"
          containerClassName={styles.input1}
          value={get(formValues, 'bank_name', '')}
          onChange={handleChange('bank_name')}
          errors={err}
          id="bank_name"
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>Account Number</p>
        <Input
          type="number"
          placeholder="A/c Number"
          containerClassName={styles.input1}
          value={get(formValues, 'acc_no', '')}
          onChange={handleChange('acc_no')}
          errors={err}
          id="acc_no"
          maxLength={10}
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>Account Holder Name</p>
        <Input
          placeholder="A/c Holder Name"
          containerClassName={styles.input1}
          value={get(formValues, 'acc_holder_name', '')}
          onChange={handleChange('acc_holder_name')}
          errors={err}
          id="acc_holder_name"
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>CIN Number</p>
        <Input
          placeholder="CIN Number"
          containerClassName={styles.input1}
          value={get(formValues, 'cin_no', '')}
          onChange={handleChange('cin_no')}
          errors={err}
          id="cin_no"
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>IBAN</p>
        <Input
          placeholder="IBAN"
          containerClassName={styles.input1}
          value={get(formValues, 'iban', '')}
          onChange={handleChange('iban')}
          errors={err}
          id="iban"
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>Swift</p>
        <Input
          placeholder="Swift Code"
          containerClassName={styles.input1}
          value={get(formValues, 'swift_code', '')}
          onChange={handleChange('swift_code')}
          errors={err}
          id="swift_code"
        />
      </div>
      <div className={styles.inputLabel}>
        <p className={styles.label}>Branch</p>
        <Input
          placeholder="Branch"
          containerClassName={styles.input1}
          value={get(formValues, 'branch_code', '')}
          onChange={handleChange('branch_code')}
          errors={err}
          id="branch_code"
          onEnterClick={handleSave}
        />
      </div>

      <Button
        className={styles.btn}
        type="Primary"
        onClick={handleSave}
        isLoading={loading}
        disabled={disableButton()}
      >
        Save
      </Button>
    </div>
  );
};

export default BankDetails;
//
